// Explicit theme choice (not OS-preference-based — a shared shop terminal
// shouldn't silently flip appearance based on device settings nobody in the
// shop actually chose). Persisted directly to localStorage under its own key
// so the blocking init script in index.html can read it synchronously before
// paint, avoiding a flash of the wrong theme.
//
// Ten palettes since 2026-10-04 (owner: the white and the grey-black both
// tire a cashier's eyes over a shift — „მოძებნე მსგავსი აპლიკაციების
// გადაწყვეტა"), eleven since 1.38 — „mist", the soft grey-blue light (owner,
// 2026-10-12: „ჩვენი თეთრი ფონები თვალზე მოქმედებს, მკვეთრია"). Two families:
// LIGHT (paper / slate / mist / sage / sand / cherry) and DARK (dark /
// graphite / ocean / amethyst / darkcherry). The `.dark` class on <html>
// selects the family — every
// `dark:` utility in the app keys off it — and `data-theme` selects the
// palette inside the family (globals.css overrides the tokens per palette).
// The header's sun/moon button flips between the families, remembering
// which palette of each was last chosen.
//
// DEFAULT = graphite (owner, 2026-09-15, after trying all five on live).
// A device that never chose gets graphite; a device that chose keeps its
// choice. index.html paints graphite before the stylesheet for the same
// reason.

export type Theme = 'light' | 'slate' | 'mist' | 'sage' | 'sand' | 'cherry' | 'dark' | 'graphite' | 'ocean' | 'amethyst' | 'darkcherry'

import type { Key } from './i18n'

export interface ThemeInfo {
  key: Theme
  /** As the settings card names it — a key, said in the interface language. */
  label: Key
  /** One line: what it is like, and who it is modelled on. */
  hint: Key
  family: 'light' | 'dark'
}

export const THEMES: ThemeInfo[] = [
  { key: 'light',    label: 'theme.light',  hint: 'theme.light.hint',                 family: 'light' },
  { key: 'slate',    label: 'theme.slate',   hint: 'theme.slate.hint',  family: 'light' },
  { key: 'mist',     label: 'theme.mist',    hint: 'theme.mist.hint',   family: 'light' },
  { key: 'sage',     label: 'theme.sage',    hint: 'theme.sage.hint',          family: 'light' },
  { key: 'sand',     label: 'theme.sand',    hint: 'theme.sand.hint',           family: 'light' },
  { key: 'cherry',   label: 'theme.cherry',  hint: 'theme.cherry.hint',         family: 'light' },
  { key: 'graphite', label: 'theme.graphite',  hint: 'theme.graphite.hint',              family: 'dark' },
  { key: 'dark',     label: 'theme.dark',     hint: 'theme.dark.hint',                    family: 'dark' },
  { key: 'ocean',    label: 'theme.ocean',   hint: 'theme.ocean.hint', family: 'dark' },
  { key: 'amethyst', label: 'theme.amethyst', hint: 'theme.amethyst.hint', family: 'dark' },
  { key: 'darkcherry', label: 'theme.darkcherry', hint: 'theme.darkcherry.hint', family: 'dark' },
]

const ALL: Theme[] = THEMES.map(t => t.key)
export const isTheme = (v: unknown): v is Theme => typeof v === 'string' && (ALL as string[]).includes(v)
export const themeFamily = (t: Theme): 'light' | 'dark' => THEMES.find(x => x.key === t)?.family ?? 'light'

/** localStorage key for the chosen theme.
 *  ⚠️ DUPLICATED IN `index.html`, which reads it in a blocking inline script
 *  before any module can load. `lib/theme.test.ts` asserts the two agree. */
export const THEME_STORAGE_KEY = 'theme'
const KEY = THEME_STORAGE_KEY
/** The last palette chosen in each family — so sun/moon brings back YOUR light, not the default. */
const PREF_LIGHT = 'theme-light'
const PREF_DARK = 'theme-dark'

export const DEFAULT_THEME: Theme = 'graphite'

/** Fired on window by applyTheme — the switches (components/theme-toggle.tsx)
 *  follow a change made elsewhere, e.g. by the first-run screen fit. */
export const THEME_EVENT = 'martepos:theme'

export function getStoredTheme(): Theme {
  if (typeof window === 'undefined') return DEFAULT_THEME
  const v = localStorage.getItem(KEY)
  return isTheme(v) ? v : DEFAULT_THEME
}

/** What <html> says right now — the toggles read this, not their own state. */
export function getCurrentTheme(): Theme {
  if (typeof document === 'undefined') return DEFAULT_THEME
  const v = document.documentElement.dataset.theme
  if (isTheme(v)) return v
  return document.documentElement.classList.contains('dark') ? DEFAULT_THEME : 'light'
}

export function applyTheme(theme: Theme) {
  const family = themeFamily(theme)
  document.documentElement.classList.toggle('dark', family === 'dark')
  document.documentElement.dataset.theme = theme
  localStorage.setItem(KEY, theme)
  localStorage.setItem(family === 'dark' ? PREF_DARK : PREF_LIGHT, theme)
  window.dispatchEvent(new Event(THEME_EVENT))
}

/** The other family's remembered palette: sun/moon. */
export function oppositeTheme(current: Theme): Theme {
  const toDark = themeFamily(current) === 'light'
  const pref = localStorage.getItem(toDark ? PREF_DARK : PREF_LIGHT)
  if (isTheme(pref) && themeFamily(pref) === (toDark ? 'dark' : 'light')) return pref
  return toDark ? DEFAULT_THEME : 'light'
}

/** Inline script text — inject as the first child of <body> to set the
 *  theme class before React hydrates/paints (prevents FOUC).
 *  (index.html carries its own copy; keep the two in step.) */
export const THEME_INIT_SCRIPT = `
(function(){
  try {
    var t = localStorage.getItem('${KEY}') || 'graphite';
    if (t === 'dark' || t === 'graphite' || t === 'ocean' || t === 'amethyst' || t === 'darkcherry') document.documentElement.classList.add('dark');
    document.documentElement.setAttribute('data-theme', t);
  } catch (e) {}
})();
`
